import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import recetaClassificationService from '../services/recetaClassificationService';

const RecetaClassificationContext = createContext(null);

/**
 * Carga una sola vez jerarquía + catálogo de insumos (GET /recetas/form-data) y expone listas derivadas
 * para categorías, subcategorías y familias sin consultas adicionales al navegar.
 * Tras mutaciones (alta/edición/baja), usar refetch() — un solo GET.
 */
export function RecetaClassificationProvider({ children }) {
  const [hierarchy, setHierarchy] = useState([]);
  /** Insumos activos + productos en proceso (salida de recetas); para buscadores sin GET /products extra */
  const [insumoProducts, setInsumoProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await recetaClassificationService.getRecetaFormData({ active_only: true });
      if (res.success && res.data && Array.isArray(res.data.hierarchy)) {
        setHierarchy(res.data.hierarchy);
        setInsumoProducts(Array.isArray(res.data.insumo_products) ? res.data.insumo_products : []);
      } else {
        setHierarchy([]);
        setInsumoProducts([]);
        setError(res.message || 'No se pudo cargar la clasificación de recetas');
      }
    } catch (e) {
      setHierarchy([]);
      setInsumoProducts([]);
      setError(e.response?.data?.message || e.message || 'Error al cargar la clasificación de recetas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(
    () =>
      hierarchy.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.name,
        description: c.description,
        is_active: c.is_active,
      })),
    [hierarchy]
  );

  const subcategories = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).map((s) => ({
          ...s,
          category: { id: c.id, code: c.code, name: c.name },
          category_id: s.category_id ?? c.id,
          receta_category_id: s.receta_category_id ?? c.id,
        }))
      ),
    [hierarchy]
  );

  const families = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).flatMap((s) =>
          (s.families || []).map((f) => ({
            ...f,
            subcategory: {
              id: s.id,
              code: s.code,
              name: s.name,
              receta_subcategory_id: s.id,
              category_id: c.id,
              receta_category_id: c.id,
            },
          }))
        )
      ),
    [hierarchy]
  );

  const value = useMemo(
    () => ({
      hierarchy,
      insumoProducts,
      loading,
      error,
      refetch: load,
      categories,
      subcategories,
      families,
    }),
    [hierarchy, insumoProducts, loading, error, load, categories, subcategories, families]
  );

  return (
    <RecetaClassificationContext.Provider value={value}>
      {children}
    </RecetaClassificationContext.Provider>
  );
}

export function useRecetaClassification() {
  const ctx = useContext(RecetaClassificationContext);
  if (!ctx) {
    throw new Error('useRecetaClassification debe usarse dentro de RecetaClassificationProvider');
  }
  return ctx;
}
